import React, { FC, useMemo } from 'react'
import Link from 'next/link'
import Carousel from 'react-multi-carousel'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import moment from 'moment'

import { buildSpaceDetailHref } from 'features/space/lib/buildSpaceDetailHref'
import { resolveCanonicalSpaceUuid } from 'features/space/lib/resolveCanonicalSpaceUuid'
import { Space } from 'state/ducks/space/models.new'

import { getSpaceAvailabilities } from 'utils/helpers'

import styles from './styles.module.css'

type Props = {
  locker: Space
}

const carouselResponsive = {
  desktop: {
    breakpoint: { max: 3000, min: 992 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 991, min: 576 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 575, min: 0 },
    items: 1,
  },
}

export const CoinLockerCard: FC<Props> = ({ locker }) => {
  const { t } = useTranslation()
  const { locale } = useRouter()
  const legacyId = locker.encrypted_id ?? locker.space_id

  const spaceDetailHref = useMemo(
    () =>
      buildSpaceDetailHref({
        locale,
        spaceType: locker.space_type,
        spaceUuid: resolveCanonicalSpaceUuid(locker.space_id, locker.encrypted_id),
        encryptedId: legacyId,
      }) ?? '#',
    [legacyId, locale, locker.encrypted_id, locker.space_id, locker.space_type]
  )

  const availabilities = useMemo(() => {
    if (locker.space_type === 'pudo') {
      const today = moment()

      const array = Array.apply(null, { length: 7 }).map((_, index) => {
        const date = today.clone().add(index, 'days').format('YYYY-MM-DD')
        const dayOfWeek = today.clone().add(index, 'days').get('day')
        const dayOfWeekLabel = today.clone().add(index, 'days').format('ddd')
        const ratio = 10
        const isHoliday = false

        return {
          date,
          day_of_week: dayOfWeek,
          day_of_week_label: dayOfWeekLabel,
          ratio,
          is_holiday: isHoliday,
        }
      })
      return array
    }

    return locker.availabilities
  }, [locker.availabilities, locker.space_type])

  return (
    <div className={styles.container}>
      <div className={styles.dataContainer}>
        <div className={styles.carouselContainer}>
          {locker.space_photos.length ? (
            <Carousel
              ssr
              responsive={carouselResponsive}
              infinite
              swipeable={false}
              arrows
              customLeftArrow={
                <button className={styles.arrowButtonLeft}>
                  <FaChevronLeft size={12} color="gray" />
                </button>
              }
              customRightArrow={
                <button className={styles.arrowButtonRight}>
                  <FaChevronRight size={12} color="gray" />
                </button>
              }
            >
              {locker.space_photos.map((photo, index) => {
                return (
                  <div key={index} className={styles.photoContainer}>
                    <img
                      src={photo.url || '/location_popular_area_default.webp'}
                      alt={photo.photo_description}
                    />
                  </div>
                )
              })}
            </Carousel>
          ) : (
            <div className={styles.photoContainer}>
              <img src="/location_popular_area_default.webp" alt="" />
            </div>
          )}
        </div>

        <div className={styles.infoContainer}>
          <h3 className={styles.spaceName}>{locker.name}</h3>
          <div className={styles.row}>
            {!!locker.nearby_stations.length && (
              <>
                <div className={styles.nearbyStationContainer}>
                  {locker.nearby_stations.map((station, index) => {
                    return (
                      <div key={index} className={styles.distanceFromStation}>
                        <img src="/map_pin_icon_gray.svg" alt="" />
                        <p>
                          {t('COMMON.HOW_MANY_DISTANCE_WALK_FROM_THE_STATION', {
                            stationName: station.station_name,
                            distance: station.distance || 0,
                          })}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
            <div>
              <div className={styles.businessHours}>
                <img src="/business_hours_icon_gray.svg" alt="" />
                <p>
                  {`${t('COMMON.TODAY_BUSINESS_TIME')} ${locker?.working_hours?.open_at || ''}〜${
                    locker?.working_hours?.close_at || ''
                  }`}
                </p>
              </div>
            </div>
          </div>
          {!!locker.capacity.length && (
            <div className={styles.capacityContainer}>
              <label>{t('COMMON.CAN_STORE_LUGGAGE_COUNT')}</label>

              <div className={styles.row}>
                {locker.capacity.map((capacity, index) => {
                  const { size, quantity } = capacity

                  let label = ''

                  if (size === 'large') {
                    label = t('LOCKER.LABEL.LARGE_SIZE')
                  } else if (size === 'small') {
                    label = t('LOCKER.LABEL.SMALL_SIZE')
                  } else if (size === 'medium') {
                    label = t('LOCKER.LABEL.MEDIUM_SIZE')
                  }

                  if (!label) return null

                  return (
                    <div key={index} className={styles.spaceCapacity}>
                      <img src="/locker_large.svg" alt="" />
                      <p>{`${label} ${quantity}`}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.availabilityContainer}>
        <div className={styles.availabilityTitle}>
          <p>{t('COMMON.AVAILABILITY_TIME')}</p>
        </div>
        <div className={styles.availabilityList}>
          {availabilities.map((availabity, index) => {
            const { day_of_week } = availabity
            const availableData = getSpaceAvailabilities(availabity)

            let holidayClasses = ''

            if (day_of_week === 6) {
              holidayClasses = styles.saturday
            } else if (day_of_week === 0) {
              holidayClasses = styles.sunday
            }

            return (
              <div key={index} className={`${styles.availability} ${holidayClasses}`}>
                <p>{availableData.date}</p>
                <img src={availableData.iconImage.src} alt={availableData.iconImage.alt} />
              </div>
            )
          })}
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <Link href={spaceDetailHref} legacyBehavior>
          <a className={styles.reserveButtonLink}>
            <span className={styles.reserveButton}>{t('COMMON.COIN_LOCKER.RESERVE')}</span>
          </a>
        </Link>
      </div>
    </div>
  )
}
